import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LogOut, Package, ShoppingBag, TrendingUp, Boxes, ChevronRight, MessageCircle, Camera, Settings, Plus, Pencil, EyeOff, Eye, Mail, RotateCcw, ShieldCheck, Trash2, Download, FileSpreadsheet, CheckSquare, Square } from "lucide-react";
import ProductForm from "@/components/ProductForm";
import StoreSettings from "@/components/StoreSettings";
import CouponsPanel from "@/components/CouponsPanel";
import GalleryPanel from "@/components/GalleryPanel";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];
const STATUS_COLORS = {
  pending: "bg-[#D97706]/10 text-[#D97706]",
  confirmed: "bg-[#D9822B]/10 text-[#D9822B]",
  shipped: "bg-[#5F7355]/10 text-[#5F7355]",
  delivered: "bg-[#2D6A4F]/10 text-[#2D6A4F]",
  cancelled: "bg-[#9E3B21]/10 text-[#9E3B21]",
};

const WA_PHONE_KEY = "da_admin_wa_phone";

function buildWhatsAppLink(phone, order) {
  const clean = (phone || "").replace(/\D/g, "");
  const giftNotes = order.items
    .filter((i) => i.is_gift_box && i.gift_note)
    .map((i) => `Gift note: "${i.gift_note}"`);
  const lines = [
    `*New Dadu's Achaar Order* - ${order.order_number}`,
    ``,
    `*Customer:* ${order.address.full_name} (+91 ${order.address.phone})`,
    `*City:* ${order.address.city}, ${order.address.state} - ${order.address.pincode}`,
    ``,
    `*Items:*`,
    ...order.items.map((i) => `- ${i.product_name} - ${i.weight} x ${i.quantity} = Rs.${i.unit_price * i.quantity}`),
    ``,
    ...giftNotes,
    giftNotes.length ? `` : null,
    `*Total:* Rs.${order.total} (${order.payment_method.toUpperCase()})`,
    ``,
    order.notes ? `*Order notes:* ${order.notes}` : null,
    ``,
    `Address: ${order.address.line1}${order.address.line2 ? ", " + order.address.line2 : ""}`,
  ].filter((l) => l !== null && l !== undefined).join("\n");
  const text = encodeURIComponent(lines);
  return clean ? `https://wa.me/${clean.startsWith("91") ? clean : "91" + clean}?text=${text}` : `https://wa.me/?text=${text}`;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [waPhone, setWaPhone] = useState(() => localStorage.getItem(WA_PHONE_KEY) || "");
  const [showSettings, setShowSettings] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [productFormOpen, setProductFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState(new Set());
  const [downloading, setDownloading] = useState(null); // 'orders' | 'report' | null
  const fileInputs = useRef({});
  const navigate = useNavigate();

  const loadAll = useCallback(async () => {
    try {
      const [s, o, p, ps] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/products"),
        api.get("/admin/payments/status").catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setOrders(o.data);
      setProducts(p.data);
      setPaymentStatus(ps.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("da_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("da_admin_token")) {
      navigate("/admin/login");
      return;
    }
    loadAll();
  }, [loadAll, navigate]);

  const updateOrderStatus = async (order_number, status) => {
    try {
      await api.patch(`/admin/orders/${order_number}`, { status });
      toast.success(`Order ${order_number} → ${status}`);
      loadAll();
      setSelectedOrder(null);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const updateStock = async (product_id, stock) => {
    try {
      await api.patch(`/admin/products/${product_id}`, { stock: parseInt(stock, 10) });
      toast.success("Stock updated");
      loadAll();
    } catch {
      toast.error("Failed to update stock");
    }
  };

  const uploadImage = async (product_id, file) => {
    if (!file) return;
    setUploadingId(product_id);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("da_admin_token");
      const res = await fetch(`${API}/admin/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      // Build absolute URL for image src
      const absUrl = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      await api.patch(`/admin/products/${product_id}`, { image_url: absUrl });
      toast.success("Photo updated");
      loadAll();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const savePhone = () => {
    const clean = waPhone.replace(/\D/g, "");
    if (clean.length < 10) {
      toast.error("Enter a valid 10-digit phone");
      return;
    }
    localStorage.setItem(WA_PHONE_KEY, clean);
    toast.success("WhatsApp phone saved");
    setShowSettings(false);
  };

  const openWhatsApp = (order) => {
    const phone = localStorage.getItem(WA_PHONE_KEY);
    if (!phone) {
      toast.info("Set your WhatsApp phone first (⚙︎ Settings)");
      setShowSettings(true);
      return;
    }
    window.open(buildWhatsAppLink(phone, order), "_blank");
  };

  const resendEmail = async (order) => {
    const suggested = order.address?.email || "";
    const target = window.prompt(
      `Resend order ${order.order_number} confirmation to:`,
      suggested
    );
    if (target === null) return;
    const clean = (target || "").trim();
    if (!clean) return;
    try {
      const res = await api.post(`/admin/orders/${order.order_number}/resend-email`, { override_email: clean });
      toast.success(`Sent to ${res.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    }
  };

  const refundOrder = async (order) => {
    if (order.razorpay_refund_id) {
      toast.info("This order has already been refunded");
      return;
    }
    const amountStr = window.prompt(
      `Refund order ${order.order_number} (paid ${formatINR(order.total)} via Razorpay).\n\nEnter refund amount in ₹ (leave blank for full refund):`,
      ""
    );
    if (amountStr === null) return;
    const trimmed = (amountStr || "").trim();
    const body = {};
    if (trimmed) {
      const amt = parseFloat(trimmed);
      if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount"); return; }
      body.amount = amt;
    }
    const reason = window.prompt(`Reason for refund (optional):`, "") || "";
    if (reason) body.reason = reason;
    const confirmMsg = body.amount
      ? `Refund ${formatINR(body.amount)} to the customer? This cannot be undone.`
      : `Refund the FULL amount ${formatINR(order.total)} to the customer? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await api.post(`/admin/orders/${order.order_number}/refund`, body);
      toast.success(`Refunded ${formatINR(res.data.refund_amount)}`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    }
  };

  const openAddProduct = () => { setEditingProduct(null); setProductFormOpen(true); };
  const openEditProduct = (p) => { setEditingProduct(p); setProductFormOpen(true); };

  const deleteOrder = async (order) => {
    const confirmMsg = `Permanently delete order ${order.order_number}?\n\nCustomer: ${order.address?.full_name || "—"}\nTotal: ${formatINR(order.total)}\n\nThis cannot be undone.${order.payment_method === "razorpay" && order.payment_status === "paid" && !order.razorpay_refund_id ? "\n\n⚠ This Razorpay order is still PAID. You'll need to refund it first." : ""}`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.delete(`/admin/orders/${order.order_number}`);
      toast.success(`Order ${order.order_number} deleted`);
      setSelectedOrderNumbers((prev) => { const n = new Set(prev); n.delete(order.order_number); return n; });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete order");
    }
  };

  const toggleSelectOrder = (orderNumber) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrderNumbers.size === orders.length && orders.length > 0) {
      setSelectedOrderNumbers(new Set());
    } else {
      setSelectedOrderNumbers(new Set(orders.map((o) => o.order_number)));
    }
  };

  const bulkDeleteSelected = async () => {
    const nums = Array.from(selectedOrderNumbers);
    if (nums.length === 0) return;
    if (!window.confirm(`Delete ${nums.length} selected order${nums.length > 1 ? "s" : ""}?\n\nPaid Razorpay orders that haven't been refunded will be SKIPPED automatically.\nThis cannot be undone.`)) return;
    try {
      const res = await api.post("/admin/orders/bulk-delete", { order_numbers: nums });
      const { deleted_count, skipped_count, not_found_count = 0 } = res.data;
      const parts = [`Deleted ${deleted_count} order${deleted_count === 1 ? "" : "s"}`];
      if (skipped_count > 0) parts.push(`Skipped ${skipped_count} (paid Razorpay — refund first)`);
      if (not_found_count > 0) parts.push(`${not_found_count} not found`);
      toast.success(parts.join(" · "));
      setSelectedOrderNumbers(new Set());
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const downloadFile = async (endpoint, kind, defaultName) => {
    setDownloading(kind);
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const fname = match ? match[1] : defaultName;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = fname;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fname}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to download ${kind}`);
    } finally {
      setDownloading(null);
    }
  };

  const exportOrdersXlsx = () => downloadFile("/admin/orders/export.xlsx", "orders", "dadus-orders.xlsx");
  const downloadReportXlsx = () => downloadFile("/admin/reports/summary.xlsx", "report", "dadus-report.xlsx");

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/products/${p.id}`, { active: !p.active });
      toast.success(p.active ? "Hidden from site" : "Now visible on site");
      loadAll();
    } catch {
      toast.error("Failed to update visibility");
    }
  };

  const logout = () => {
    localStorage.removeItem("da_admin_token");
    navigate("/admin/login");
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-16">Loading dashboard...</div>;

  return (
    <div className="fade-up max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#9E3B21] font-semibold">✦ Admin Dashboard</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold">Dadu's Kitchen HQ</h1>
          {paymentStatus?.razorpay && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span
                data-testid="razorpay-mode-badge"
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider ${
                  paymentStatus.razorpay.mode === "live"
                    ? "bg-[#2D6A4F] text-white"
                    : paymentStatus.razorpay.mode === "test"
                    ? "bg-[#D9822B]/15 text-[#D9822B]"
                    : "bg-[#B8B0A6]/20 text-[#6E675F]"
                }`}
              >
                <ShieldCheck className="w-3 h-3" />
                Razorpay: {paymentStatus.razorpay.mode}
              </span>
              {paymentStatus.razorpay.mode === "live" && !paymentStatus.razorpay.webhook_configured && (
                <span
                  data-testid="razorpay-webhook-warning"
                  className="text-[11px] text-[#D9822B] font-medium"
                  title="Configure RAZORPAY_WEBHOOK_SECRET and add webhook in Razorpay dashboard for auto-updates"
                >
                  ⚠ Webhook not configured
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button data-testid="admin-settings" onClick={() => setShowSettings(true)} variant="outline" className="rounded-full">
            <Settings className="w-4 h-4 mr-2" /> Settings
          </Button>
          <Button data-testid="admin-logout" onClick={logout} variant="outline" className="rounded-full">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Orders", value: stats?.total_orders || 0, icon: ShoppingBag, color: "#9E3B21" },
          { label: "Revenue", value: formatINR(stats?.revenue || 0), icon: TrendingUp, color: "#2D6A4F" },
          { label: "Pending", value: stats?.pending_orders || 0, icon: Package, color: "#D9822B" },
          { label: "Active Products", value: stats?.active_products || 0, icon: Boxes, color: "#5F7355" },
        ].map((s) => (
          <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`} className="bg-white border border-[#E6DEC3] rounded-2xl p-5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: `${s.color}18` }}>
              <s.icon className="w-4 h-4" style={{ color: s.color }} />
            </div>
            <div className="text-xs text-[#6E675F] uppercase tracking-wider">{s.label}</div>
            <div className="font-serif text-2xl font-semibold mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="orders" className="w-full">
        <TabsList className="bg-[#F4EFE6] flex flex-wrap h-auto">
          <TabsTrigger data-testid="tab-orders" value="orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-products" value="products">Products ({products.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-coupons" value="coupons">Coupons</TabsTrigger>
          <TabsTrigger data-testid="tab-gallery" value="gallery">Gallery</TabsTrigger>
          <TabsTrigger data-testid="tab-settings" value="settings">Store Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          <div className="mb-4 flex flex-wrap items-center gap-3 bg-white border border-[#E6DEC3] rounded-2xl p-4">
            <button
              data-testid="orders-select-all"
              type="button"
              onClick={toggleSelectAll}
              className="inline-flex items-center gap-2 text-sm text-[#4A423C] hover:text-[#9E3B21]"
              title={selectedOrderNumbers.size === orders.length && orders.length > 0 ? "Clear selection" : "Select all"}
            >
              {selectedOrderNumbers.size === orders.length && orders.length > 0 ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
              <span>{selectedOrderNumbers.size > 0 ? `${selectedOrderNumbers.size} selected` : "Select all"}</span>
            </button>
            {selectedOrderNumbers.size > 0 && (
              <Button
                data-testid="orders-bulk-delete"
                variant="outline"
                size="sm"
                className="border-[#B03A2E] text-[#B03A2E] hover:bg-[#B03A2E] hover:text-white"
                onClick={bulkDeleteSelected}
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedOrderNumbers.size}
              </Button>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Button
                data-testid="orders-export-xlsx"
                variant="outline"
                size="sm"
                className="border-[#5F7355] text-[#5F7355] hover:bg-[#5F7355] hover:text-white"
                onClick={exportOrdersXlsx}
                disabled={downloading === "orders"}
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                {downloading === "orders" ? "Preparing…" : "Export Orders (Excel)"}
              </Button>
              <Button
                data-testid="admin-download-report"
                variant="outline"
                size="sm"
                className="border-[#9E3B21] text-[#9E3B21] hover:bg-[#9E3B21] hover:text-white"
                onClick={downloadReportXlsx}
                disabled={downloading === "report"}
              >
                <Download className="w-4 h-4 mr-1" />
                {downloading === "report" ? "Preparing…" : "Download Report"}
              </Button>
            </div>
          </div>
          <div className="bg-white border border-[#E6DEC3] rounded-2xl overflow-hidden">
            {orders.length === 0 ? (
              <p className="p-8 text-center text-[#6E675F]">No orders yet.</p>
            ) : (
              <div className="divide-y divide-[#E6DEC3]">
                {orders.map((o) => (
                  <div key={o.order_number} data-testid={`order-row-${o.order_number}`} className={`p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:bg-[#FBF8F3] ${selectedOrderNumbers.has(o.order_number) ? "bg-[#FEF3E9]" : ""}`}>
                    <button
                      data-testid={`order-select-${o.order_number}`}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleSelectOrder(o.order_number); }}
                      className="text-[#4A423C] hover:text-[#9E3B21] flex-shrink-0"
                      title="Select for bulk actions"
                    >
                      {selectedOrderNumbers.has(o.order_number) ? <CheckSquare className="w-5 h-5 text-[#9E3B21]" /> : <Square className="w-5 h-5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-mono text-sm font-semibold">{o.order_number}</span>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                      </div>
                      <div className="text-sm text-[#4A423C]">{o.address.full_name} · {o.address.city} · {o.items.length} item{o.items.length>1?'s':''}</div>
                      <div className="text-xs text-[#6E675F] mt-0.5">{new Date(o.created_at).toLocaleString()}</div>
                    </div>
                    <div className="font-serif text-lg font-semibold">{formatINR(o.total)}</div>
                    <Select value={o.status} onValueChange={(v) => updateOrderStatus(o.order_number, v)}>
                      <SelectTrigger data-testid={`order-status-select-${o.order_number}`} className="w-full sm:w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button
                      data-testid={`notify-whatsapp-${o.order_number}`}
                      variant="outline"
                      size="sm"
                      className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white"
                      onClick={() => openWhatsApp(o)}
                    >
                      <MessageCircle className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Notify</span>
                    </Button>
                    <Button
                      data-testid={`resend-email-${o.order_number}`}
                      variant="outline"
                      size="sm"
                      className={`border-[#9E3B21] text-[#9E3B21] hover:bg-[#9E3B21] hover:text-white ${o.email_error ? 'ring-1 ring-[#D9822B]' : ''}`}
                      onClick={() => resendEmail(o)}
                      title={o.email_error ? `Last error: ${o.email_error}` : (o.email_sent ? `Sent ${o.email_sent_at || ''}` : "Send confirmation email")}
                    >
                      <Mail className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">{o.email_error ? "Retry" : "Email"}</span>
                    </Button>
                    {o.payment_method === "razorpay" && o.payment_status === "paid" && !o.razorpay_refund_id && (
                      <Button
                        data-testid={`refund-order-${o.order_number}`}
                        variant="outline"
                        size="sm"
                        className="border-[#D9822B] text-[#D9822B] hover:bg-[#D9822B] hover:text-white"
                        onClick={() => refundOrder(o)}
                      >
                        <RotateCcw className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Refund</span>
                      </Button>
                    )}
                    {o.razorpay_refund_id && (
                      <span data-testid={`refunded-badge-${o.order_number}`} className="text-[11px] px-2 py-1 rounded-full bg-[#B8B0A6]/20 text-[#6E675F] font-semibold uppercase tracking-wider">
                        Refunded {formatINR(o.refund_amount || 0)}
                      </span>
                    )}
                    <Button
                      data-testid={`delete-order-${o.order_number}`}
                      variant="ghost"
                      size="sm"
                      className="text-[#B03A2E] hover:bg-[#B03A2E] hover:text-white"
                      onClick={(e) => { e.stopPropagation(); deleteOrder(o); }}
                      title="Delete this order"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button data-testid={`view-order-${o.order_number}`} variant="ghost" size="sm" onClick={() => setSelectedOrder(o)}>
                      View <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
            <p className="text-sm text-[#6E675F]">Edit any product below or add a new one when a fresh batch is ready.</p>
            <Button data-testid="add-product-btn" onClick={openAddProduct} className="btn-primary rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.slug}`} className={`bg-white border rounded-2xl p-4 flex gap-3 ${p.active ? "border-[#E6DEC3]" : "border-[#D97706] opacity-70"}`}>
                <div className="relative w-20 h-20 flex-shrink-0 group">
                  <img src={p.image_url} alt={p.name} className="w-20 h-20 rounded-lg object-cover" />
                  <button
                    data-testid={`upload-photo-${p.slug}`}
                    onClick={() => fileInputs.current[p.id]?.click()}
                    disabled={uploadingId === p.id}
                    className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Upload new photo"
                  >
                    {uploadingId === p.id ? (
                      <span className="text-[10px] text-white">Uploading…</span>
                    ) : (
                      <Camera className="w-5 h-5 text-white" />
                    )}
                  </button>
                  <input
                    ref={(el) => (fileInputs.current[p.id] = el)}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadImage(p.id, e.target.files?.[0])}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="font-serif font-semibold text-[#1E1B18] leading-tight truncate">{p.name}</p>
                      <p className="text-xs text-[#6E675F] truncate">{p.subtitle}</p>
                    </div>
                    {!p.active && <span className="text-[9px] uppercase tracking-wider bg-[#D97706]/20 text-[#D97706] px-2 py-0.5 rounded-full font-semibold flex-shrink-0">Hidden</span>}
                  </div>
                  <div className="mt-1.5 text-xs text-[#4A423C]">
                    {Object.entries(p.prices || {}).map(([w, price]) => (
                      <span key={w} className="mr-2">{w}: <strong>{formatINR(price)}</strong></span>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Input
                      data-testid={`stock-input-${p.slug}`}
                      type="number"
                      defaultValue={p.stock}
                      className="h-8 w-20 text-sm"
                      onBlur={(e) => e.target.value !== String(p.stock) && updateStock(p.id, e.target.value)}
                    />
                    <span className="text-xs text-[#6E675F]">in stock</span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button data-testid={`edit-product-${p.slug}`} onClick={() => openEditProduct(p)} size="sm" variant="outline" className="h-7 text-xs rounded-full">
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button data-testid={`toggle-active-${p.slug}`} onClick={() => toggleActive(p)} size="sm" variant="ghost" className="h-7 text-xs">
                      {p.active ? <><EyeOff className="w-3 h-3 mr-1" /> Hide</> : <><Eye className="w-3 h-3 mr-1" /> Show</>}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <StoreSettings />
        </TabsContent>

        <TabsContent value="coupons" className="mt-6">
          <CouponsPanel />
        </TabsContent>

        <TabsContent value="gallery" className="mt-6">
          <GalleryPanel />
        </TabsContent>
      </Tabs>

      <ProductForm
        open={productFormOpen}
        onOpenChange={setProductFormOpen}
        product={editingProduct}
        onSaved={loadAll}
      />

      {/* Settings modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md bg-white" data-testid="settings-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Admin Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#6E675F]">WhatsApp Alert Phone</label>
              <Input
                data-testid="settings-wa-phone"
                value={waPhone}
                onChange={(e) => setWaPhone(e.target.value)}
                placeholder="10-digit phone (e.g. 9876543210)"
                maxLength={13}
                className="mt-1.5"
              />
              <p className="text-xs text-[#6E675F] mt-1.5">This number will receive order alerts when you click "Notify" on a new order. Stored locally in your browser.</p>
            </div>
            <Button data-testid="settings-save" onClick={savePhone} className="btn-primary rounded-full w-full">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedOrder} onOpenChange={(o) => !o && setSelectedOrder(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="order-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#6E675F] mb-1">Customer</p>
                <p className="text-sm">{selectedOrder.address.full_name} · +91 {selectedOrder.address.phone}</p>
                {selectedOrder.address.email && <p className="text-sm">{selectedOrder.address.email}</p>}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#6E675F] mb-1">Address</p>
                <p className="text-sm">
                  {selectedOrder.address.line1}{selectedOrder.address.line2 ? `, ${selectedOrder.address.line2}` : ""}<br />
                  {selectedOrder.address.city}, {selectedOrder.address.state} - {selectedOrder.address.pincode}
                  {selectedOrder.address.landmark && <><br />Landmark: {selectedOrder.address.landmark}</>}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#6E675F] mb-1">Items</p>
                <ul className="space-y-2 text-sm">
                  {selectedOrder.items.map((i, idx) => (
                    <li key={`${i.product_id || i.product_name}-${i.weight}-${idx}`}>
                      <div className="flex justify-between">
                        <span>{i.product_name} · {i.weight} × {i.quantity}</span>
                        <span>{formatINR(i.unit_price * i.quantity)}</span>
                      </div>
                      {i.is_gift_box && i.gift_note ? (
                        <div className="mt-1 text-xs text-[#5F7355] italic bg-[#5F7355]/5 rounded-lg px-3 py-2">
                          🎁 Gift note (print on card): "{i.gift_note}"
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-t border-[#E6DEC3] pt-3 flex justify-between font-serif text-lg font-semibold">
                <span>Total ({selectedOrder.payment_method.toUpperCase()})</span>
                <span>{formatINR(selectedOrder.total)}</span>
              </div>
              {selectedOrder.notes && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#6E675F] mb-1">Notes</p>
                  <p className="text-sm italic">"{selectedOrder.notes}"</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
